import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  badgeClass,
  formLabelClass,
  formControlClass,
  btnPrimaryClass,
  alertErrorClass,
  spinnerClass,
} from "./dashboardStyles";
import TabList, { TabPanel } from "./TabList";
import { REMINDER_OFF, reminderOffsetKey } from "../../constants/notifications";
import { NOTIFICATION_CHANNEL_TABS } from "../../constants/settings";
import { SMS_CENTER_PATH } from "../../constants/sms";

/**
 * One switch plus its explanation.
 *
 * The description is not decoration: every row here decides whether a real
 * person receives an email about an appointment they are relying on, and "Send
 * reminders" alone does not say who gets them or when.
 *
 * A native checkbox under `role="switch"`, so it keeps the checkbox's keyboard
 * and form behaviour; the track beside it is drawn from its state. The whole
 * row is the label, which makes the touch target the row rather than the 44px
 * track.
 */
export const SwitchRow = ({ id, checked, disabled, onChange, label, description, icon }) => (
  <label
    htmlFor={id}
    className={`flex min-h-11 items-center gap-4 py-4 ${
      disabled ? "cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    {icon && <i className={`bi ${icon} text-lg text-ink-500`} aria-hidden="true" />}
    <span className="min-w-0 flex-1">
      <span id={`${id}-label`} className="block text-fluid-sm font-semibold text-ink-900">
        {label}
      </span>
      {description && (
        <span id={`${id}-help`} className="mt-0.5 block text-xs text-ink-500">
          {description}
        </span>
      )}
    </span>
    <input
      id={id}
      name={id}
      type="checkbox"
      role="switch"
      checked={checked}
      disabled={disabled}
      aria-labelledby={`${id}-label`}
      aria-describedby={description ? `${id}-help` : undefined}
      onChange={(event) => onChange(event.target.checked)}
      className="peer sr-only"
    />
    <span
      aria-hidden="true"
      className="relative h-6 w-11 shrink-0 rounded-full bg-ink-200 transition peer-checked:bg-brand-600 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600 peer-disabled:opacity-60 after:absolute after:top-0.5 after:left-0.5 after:size-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-5 motion-reduce:transition-none motion-reduce:after:transition-none"
    />
  </label>
);

SwitchRow.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.string,
};

/**
 * Everything the two Settings tabs built on the notification settings show
 * instead of their form: the spinner, the load error, and the "finish setting
 * up your business" notice for an organization that is still mid-onboarding.
 * Renders nothing once there is a form, which is the tab's own to render.
 */
export const NotificationSettingsStatus = ({ settings }) => {
  const { t } = useTranslation();
  const { loading, error, form } = settings;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className={spinnerClass} role="status" />
      </div>
    );
  }
  if (error) {
    return (
      <div className={alertErrorClass} role="alert">
        {t(error)}
      </div>
    );
  }
  if (!form) {
    return (
      <p className="m-0 text-fluid-sm text-ink-500">
        {t("Dashboard.Notifications.noOrganization")}
      </p>
    );
  }
  return null;
};

NotificationSettingsStatus.propTypes = {
  settings: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    form: PropTypes.object,
  }).isRequired,
};

/** "View only", for an employee looking at what their employer decided. */
export const ReadOnlyBadge = () => {
  const { t } = useTranslation();
  return (
    <span className={`${badgeClass} bg-ink-100 text-ink-600`}>
      {t("Dashboard.Notifications.readOnly")}
    </span>
  );
};

/**
 * The Notifications tab of the Settings page: what the business sends, one
 * channel at a time.
 *
 * Configures what the business sends, so it is only meaningful to someone who
 * belongs to a business — Settings does not show it to anyone else. Employees
 * can read the settings but not change them: this is company policy deciding
 * what every customer of the business receives, not a personal preference.
 *
 * The form itself is `useNotificationSettings`, held by the Settings page,
 * because the Language & time zone tab edits the same record.
 */
const NotificationPreferencesPanel = ({ settings, canEdit, channel, onChannelChange }) => {
  const { t } = useTranslation();
  const { form, setField, allowedOffsets, loading, saving, save } = settings;

  const handleSubmit = (event) => {
    event.preventDefault();
    save();
  };

  // The first reminder also times the SMS one, so it stays editable while
  // either channel reminds; the second is email only.
  const reminderOffsetEnabled = (name) =>
    form.customerReminderEmail ||
    (name === "reminderPrimaryMinutes" && form.customerReminderSms);

  const renderOffsetSelect = (name, value, excluded) => (
    <select
      id={name}
      name={name}
      className={formControlClass}
      value={value ?? REMINDER_OFF}
      disabled={!canEdit || !reminderOffsetEnabled(name)}
      onChange={(event) =>
        setField(
          name,
          event.target.value === REMINDER_OFF
            ? null
            : Number(event.target.value),
        )
      }
    >
      <option value={REMINDER_OFF}>
        {t("Dashboard.Notifications.noReminder")}
      </option>
      {allowedOffsets
        .filter((minutes) => minutes !== excluded)
        .map((minutes) => (
          <option key={minutes} value={minutes}>
            {t(reminderOffsetKey(minutes))}
          </option>
        ))}
    </select>
  );

  const switchRow = (id, labelKey, descriptionKey, icon) => (
    <SwitchRow
      id={id}
      checked={form[id]}
      disabled={!canEdit}
      onChange={(value) => setField(id, value)}
      label={t(labelKey)}
      description={descriptionKey && t(descriptionKey)}
      icon={icon}
    />
  );

  return (
    <>
      <div className={cardClass}>
        <div className={cardHeaderClass}>
          <h3 className={cardTitleClass}>
            {t("Dashboard.Settings.notificationPreferences")}
          </h3>
          {!canEdit && !loading && form && <ReadOnlyBadge />}
        </div>

        <div className={cardBodyClass}>
          <NotificationSettingsStatus settings={settings} />
          {!loading && form && (
            <form onSubmit={handleSubmit}>
              <TabList
                tabs={NOTIFICATION_CHANNEL_TABS}
                active={channel}
                onChange={onChannelChange}
                label={t("Dashboard.Notifications.channels.label")}
                idPrefix="notification-channel"
                variant="underline"
              />

              <TabPanel
                idPrefix="notification-channel"
                id="email"
                active={channel}
                className="divide-y divide-ink-100"
              >
                {switchRow(
                  "customerConfirmationEmail",
                  "Dashboard.Notifications.confirmation",
                  "Dashboard.Notifications.confirmationHelp",
                )}
                {switchRow(
                  "customerChangeEmail",
                  "Dashboard.Notifications.change",
                  "Dashboard.Notifications.changeHelp",
                )}
                <div className="pb-4">
                  {switchRow(
                    "customerReminderEmail",
                    "Dashboard.Notifications.reminder",
                    "Dashboard.Notifications.reminderHelp",
                  )}
                  <div className="grid gap-4 rounded-lg bg-brand-50 p-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label className={formLabelClass} htmlFor="reminderPrimaryMinutes">
                        {t("Dashboard.Notifications.firstReminder")}
                      </label>
                      {renderOffsetSelect(
                        "reminderPrimaryMinutes",
                        form.reminderPrimaryMinutes,
                        form.reminderSecondaryMinutes,
                      )}
                    </div>
                    <div className="min-w-0">
                      <label className={formLabelClass} htmlFor="reminderSecondaryMinutes">
                        {t("Dashboard.Notifications.secondReminder")}
                      </label>
                      {renderOffsetSelect(
                        "reminderSecondaryMinutes",
                        form.reminderSecondaryMinutes,
                        form.reminderPrimaryMinutes,
                      )}
                      <p className="mt-1 text-xs text-ink-500">
                        {t("Dashboard.Notifications.secondReminderHelp")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="py-4">
                  <label className={formLabelClass} htmlFor="replyToEmail">
                    {t("Dashboard.Notifications.replyTo")}
                  </label>
                  <input
                    id="replyToEmail"
                    name="replyToEmail"
                    type="email"
                    className={formControlClass}
                    value={form.replyToEmail}
                    disabled={!canEdit}
                    placeholder={t("Dashboard.Notifications.replyToPlaceholder")}
                    onChange={(event) => setField("replyToEmail", event.target.value)}
                  />
                  <p className="mt-1 text-xs text-ink-500">
                    {t("Dashboard.Notifications.replyToHelp")}
                  </p>
                </div>

                {switchRow(
                  "staffEmailOnNewBooking",
                  "Dashboard.Notifications.staffEmailNewBooking",
                  "Dashboard.Notifications.staffEmailNewBookingHelp",
                  "bi-bell",
                )}
              </TabPanel>

              <TabPanel idPrefix="notification-channel" id="browser" active={channel}>
                <div className="flex items-start gap-2 pt-5 pb-4">
                  <i className="bi bi-bell text-lg text-ink-500" aria-hidden="true" />
                  <div className="min-w-0">
                    <h4 className="m-0 text-fluid-sm font-semibold text-ink-900">
                      {t("Dashboard.Notifications.browserChannel")}
                    </h4>
                    <p className="mt-0.5 mb-0 text-xs text-ink-500">
                      {t("Dashboard.Notifications.browserIntro")}
                    </p>
                  </div>
                </div>
                <div className="divide-y divide-ink-100 border-t border-ink-100">
                  {switchRow(
                    "staffBrowserOnNewBooking",
                    "Dashboard.Notifications.browserNewBooking",
                  )}
                  {switchRow(
                    "staffBrowserOnCancellation",
                    "Dashboard.Notifications.browserCancellation",
                  )}
                </div>
              </TabPanel>

              {/* SMS costs the business credit per message, so these switches
                  are off until somebody turns them on (only the reminder starts
                  on, for a business on a plan with SMS), and the tab says where
                  the credit is. */}
              <TabPanel idPrefix="notification-channel" id="sms" active={channel}>
                <p className="mb-0 pt-5 pb-4 text-xs text-ink-500">
                  {t("Dashboard.Notifications.sms.intro")}{" "}
                  {canEdit ? (
                    <Link
                      to={SMS_CENTER_PATH}
                      className="font-semibold text-brand-700 underline"
                    >
                      {t("Dashboard.Notifications.sms.link")}
                    </Link>
                  ) : (
                    t("Dashboard.Notifications.sms.link")
                  )}
                  .
                </p>
                <div className="divide-y divide-ink-100 border-t border-ink-100">
                  {switchRow(
                    "customerConfirmationSms",
                    "Dashboard.Notifications.sms.confirmation",
                    "Dashboard.Notifications.sms.confirmationHelp",
                  )}
                  {switchRow(
                    "customerChangeSms",
                    "Dashboard.Notifications.sms.change",
                    "Dashboard.Notifications.sms.changeHelp",
                  )}
                  {/* The SMS reminder has no time of its own: it goes at the
                      first reminder's offset, which lives on the Email tab. So
                      the help says the offset itself rather than "at the first
                      reminder", which nobody on this tab can see. */}
                  <SwitchRow
                    id="customerReminderSms"
                    checked={form.customerReminderSms}
                    disabled={!canEdit}
                    onChange={(value) => setField("customerReminderSms", value)}
                    label={t("Dashboard.Notifications.sms.reminder")}
                    description={
                      form.reminderPrimaryMinutes
                        ? t("Dashboard.Notifications.sms.reminderHelp", {
                            when: t(reminderOffsetKey(form.reminderPrimaryMinutes)),
                          })
                        : t("Dashboard.Notifications.sms.reminderHelpNoTime")
                    }
                  />
                </div>
              </TabPanel>

              {/* On a phone the button is pinned to the bottom of the screen:
                  the email channel runs to several screens there, and a save
                  that only exists below all of it is one people miss. */}
              {canEdit && (
                <div className="flex justify-end border-t border-ink-100 pt-5 max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-30 max-sm:border-ink-200 max-sm:bg-white/95 max-sm:px-4 max-sm:py-3 max-sm:backdrop-blur">
                  <button
                    type="submit"
                    className={`${btnPrimaryClass} min-h-11 max-sm:w-full`}
                    disabled={saving}
                  >
                    {saving
                      ? t("Dashboard.Notifications.saving")
                      : t("Dashboard.Notifications.save")}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Room under the card for the pinned button to sit on, so it never
          covers the last switch. */}
      {canEdit && form && <div aria-hidden="true" className="h-16 sm:hidden" />}
    </>
  );
};

NotificationPreferencesPanel.propTypes = {
  settings: PropTypes.shape({
    form: PropTypes.object,
    setField: PropTypes.func.isRequired,
    allowedOffsets: PropTypes.arrayOf(PropTypes.number).isRequired,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    error: PropTypes.string,
    save: PropTypes.func.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool,
  channel: PropTypes.string.isRequired,
  onChannelChange: PropTypes.func.isRequired,
};

export default NotificationPreferencesPanel;
